import { useCallback, useMemo } from "react";
import {
  Table,
  Group,
  Badge,
  Text,
  Button,
  ActionIcon,
  Stack,
  Box,
  Divider,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconStethoscope,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import type { CorrectionQueueItem } from "../../services/correctionRequestService";
import { correctionRefLabel, errorRefLabel } from "../../utils/formatters";
import { formatDateOnlyWithShortMonth } from "../../utils/dateOnly";
import { CorrectionStatusBadge } from "./CorrectionStatusBadge";

const FIELD_LABELS: Record<string, string> = {
  "patient.firstName": "First Name",
  "patient.lastName": "Last Name",
  "patient.dateOfBirth": "Date of Birth",
  "patient.sex": "Sex",
  "patient.medicalRecordNumber": "Medical Record Number",
  "contact.mobilePhone": "Mobile Phone",
  "contact.homePhone": "Home Phone",
  "contact.workPhone": "Work Phone",
  "contact.email": "Email",
  "address.street": "Street Address",
  "address.state": "State",
  "address.city": "City",
  "address.zipCode": "ZIP Code",
  "primaryInsurance.policyNumber": "Policy Number (Primary)",
  "primaryInsurance.authorizationNumber": "Authorization # (Primary)",
  "primaryInsurance.groupNumber": "Group Number (Primary)",
  "primaryInsurance.groupName": "Group Name (Primary)",
  "primaryInsurance.patientRelationToInsured": "Patient Relation (Primary)",
  "secondaryInsurance.policyNumber": "Policy Number (Secondary)",
  "secondaryInsurance.authorizationNumber": "Authorization # (Secondary)",
  "secondaryInsurance.groupNumber": "Group Number (Secondary)",
  "secondaryInsurance.groupName": "Group Name (Secondary)",
  "secondaryInsurance.patientRelationToInsured": "Patient Relation (Secondary)",
  "caseInfo.externalCaseNumber": "External Case Number",
  "caseInfo.dateReceived": "Date Received",
  "caseInfo.dateCollected": "Date Collected",
  "caseInfo.clinicalHistory": "Clinical History",
  "caseInfo.indications": "Indications",
  "caseInfo.impressions": "Impressions",
  "caseInfo.originatingPracticeId": "Originating Practice",
  "caseInfo.consultationReason": "Consultation Reason",
};

interface Props {
  item: CorrectionQueueItem;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  onNavigateToReview: (caseId: string) => void;
  onNavigateToError: (caseId: string) => void;
}

export function CorrectionRow({
  item,
  isExpanded,
  onToggle,
  onNavigateToReview,
  onNavigateToError,
}: Props) {
  const hasDetail = useMemo(
    () =>
      item.hasNote ||
      (item.proposedChanges && Object.keys(item.proposedChanges).length > 0),
    [item.hasNote, item.proposedChanges]
  );

  const handleToggle = useCallback(() => {
    if (hasDetail) {
      onToggle(item.id);
    }
  }, [hasDetail, onToggle, item.id]);

  const handleNavigateToError = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onNavigateToError(item.caseId);
    },
    [onNavigateToError, item.caseId]
  );

  const handleNavigateToReview = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onNavigateToReview(item.caseId);
    },
    [onNavigateToReview, item.caseId]
  );

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const diffEntries = useMemo(() => {
    if (!item.proposedChanges) return [];
    return Object.entries(item.proposedChanges).filter(
      ([, diff]) => String(diff.before ?? "") !== String(diff.after ?? "")
    );
  }, [item.proposedChanges]);

  return (
    <>
      <Table.Tr
        style={{ cursor: hasDetail ? "pointer" : undefined }}
        onClick={handleToggle}
      >
        <Table.Td style={{ width: 28 }}>
          {hasDetail && (
            <ActionIcon variant="subtle" color="gray" size="xs">
              {isExpanded ? (
                <IconChevronDown size={14} />
              ) : (
                <IconChevronRight size={14} />
              )}
            </ActionIcon>
          )}
        </Table.Td>
        <Table.Td style={{ width: 56 }}>
          {correctionRefLabel(item.refNumber) && (
            <Badge size="xs" variant="outline" color="blue">
              {correctionRefLabel(item.refNumber)}
            </Badge>
          )}
        </Table.Td>
        <Table.Td>{item.patientName ?? "—"}</Table.Td>
        <Table.Td>
          <Text size="sm" ff="monospace">
            {item.caseNumber ?? "—"}
          </Text>
          {item.caseErrorReportId && (
            <Button
              variant="subtle"
              size="compact-xs"
              color="red"
              mt={2}
              onClick={handleNavigateToError}
            >
              {item.caseErrorReportRefNumber != null
                ? `Linked to Error ${errorRefLabel(
                    item.caseErrorReportRefNumber
                  )}`
                : "Linked to Error"}
            </Button>
          )}
        </Table.Td>
        <Table.Td>
          <Text size="sm">{item.accessionedByName ?? "—"}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{item.correctedByName ?? "—"}</Text>
        </Table.Td>
        <Table.Td ta="center">{item.proposedFieldCount}</Table.Td>
        <Table.Td style={{ whiteSpace: "nowrap" }}>
          {item.dateReceived
            ? formatDateOnlyWithShortMonth(item.dateReceived)
            : "—"}
        </Table.Td>
        <Table.Td style={{ whiteSpace: "nowrap" }}>
          {item.dateCollected
            ? formatDateOnlyWithShortMonth(item.dateCollected)
            : "—"}
        </Table.Td>
        <Table.Td style={{ whiteSpace: "nowrap" }}>
          {item.dateReported
            ? formatDateOnlyWithShortMonth(item.dateReported)
            : "—"}
        </Table.Td>
        <Table.Td>
          <Text size="sm">
            {dayjs(item.correctedAt).format("MMM D, YYYY h:mm A")}
          </Text>
        </Table.Td>
        <Table.Td style={{ width: 160 }}>
          <CorrectionStatusBadge status={item.status} />
        </Table.Td>
        <Table.Td style={{ width: 160 }} onClick={stopPropagation}>
          <Group gap="xs" wrap="nowrap">
            <Button
              size="xs"
              variant="light"
              color="blue"
              leftSection={<IconStethoscope size={13} />}
              onClick={handleNavigateToReview}
            >
              See Report
            </Button>
          </Group>
        </Table.Td>
      </Table.Tr>

      {hasDetail && isExpanded && (
        <Table.Tr style={{ background: "var(--mantine-color-gray-0)" }}>
          <Table.Td colSpan={13} p="md">
            <Stack gap="sm">
              {item.hasNote && item.correctionNote && (
                <Box>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
                    Note
                  </Text>
                  <Box
                    className="widget-rich-text"
                    dangerouslySetInnerHTML={{ __html: item.correctionNote }}
                    style={{ fontSize: 13 }}
                  />
                </Box>
              )}
              {diffEntries.length > 0 && (
                <>
                  {item.hasNote && item.correctionNote && <Divider />}
                  <Box>
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={6}>
                      Fields Changed
                    </Text>
                    <Stack gap={4}>
                      {diffEntries.map(([field, diff]) => (
                        <Group
                          key={field}
                          gap="xs"
                          wrap="nowrap"
                          align="flex-start"
                        >
                          <Text
                            size="xs"
                            c="dimmed"
                            style={{ minWidth: 180, flexShrink: 0 }}
                          >
                            {FIELD_LABELS[field] ?? field}
                          </Text>
                          <Box
                            className="rich-text-display"
                            style={{
                              color: "var(--mantine-color-red-6)",
                              textDecoration: "line-through",
                              fontSize: 12,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: String(diff.before ?? "") || "(empty)",
                            }}
                          />
                          <Text size="xs" style={{ flexShrink: 0 }}>
                            →
                          </Text>
                          <Box
                            className="rich-text-display"
                            style={{
                              color: "var(--mantine-color-green-7)",
                              fontWeight: 500,
                              fontSize: 12,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: String(diff.after ?? "") || "(empty)",
                            }}
                          />
                        </Group>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}
